import { createStore } from './store-factory';
import { Notification } from '@amzn/finspace-service-typescript-client';
import { FinSpaceServiceApi } from '../api/finspaceServiceApi';
import { logger } from '../utils/logger';

interface NotificationWithOptimistic extends Notification {
  isOptimisticallyRead?: boolean;
}

interface NotificationBellState {
  notifications: NotificationWithOptimistic[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  error: string | null;
  recommendedNextPollingIntervalSeconds: number;
  pollingTimerId: NodeJS.Timeout | null;
  
  // Actions
  loadInitialNotifications: () => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
  markAsSeen: (notificationIds: string[]) => Promise<void>;
  getUnreadNotifications: () => NotificationWithOptimistic[];
  refreshUnreadCount: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

export const useNotificationBellStore = createStore<NotificationBellState>(
  'notification-bell',
  (set, get) => ({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    hasMore: true,
    error: null,
    recommendedNextPollingIntervalSeconds: 300, // Default to 5 minutes
    pollingTimerId: null,
    
    // Initial load - fetch first page
    loadInitialNotifications: async () => {
      if (get().isLoading) return;
      
      set({ isLoading: true, error: null });
      try {
        const response = await FinSpaceServiceApi.getNotifications();
        const notifications = response.notifications || [];
        
        // Calculate unread count from notifications array
        const unreadCount = notifications.filter(n => !n.seenAt).length;
        
        set({
          notifications,
          unreadCount,
          hasMore: !!response.nextToken, // hasMore is true if nextToken is present
          recommendedNextPollingIntervalSeconds: response.recommendedNextPollingIntervalSeconds || 300,
          isLoading: false,
        });
        
        // Restart polling with new interval
        get().stopPolling();
        get().startPolling();
      } catch (error) {
        logger.error('Failed to load notifications:', error as Error);
        set({ error: 'Failed to load notifications', isLoading: false });
      }
    },
    
    // Infinite scroll - load more
    loadMoreNotifications: async () => {
      const { notifications, hasMore, isLoading } = get();
      
      if (!hasMore || isLoading) return;
      
      set({ isLoading: true });
      try {
        // Get the oldest notification's createdAt timestamp for pagination
        const oldestNotification = notifications[notifications.length - 1];
        const olderThan = oldestNotification?.createdAt;
        
        const response = await FinSpaceServiceApi.getNotifications({
          olderThan,
        });
        
        set(state => {
          // Don't recalculate unreadCount here - it should only represent new notifications
          // from initial load, not all historical unread ones loaded during pagination
          return {
            notifications: [...state.notifications, ...(response.notifications || [])],
            hasMore: !!response.nextToken,
            isLoading: false,
          };
        });
      } catch (error) {
        logger.error('Failed to load more notifications:', error as Error);
        set({ error: 'Failed to load more', isLoading: false });
      }
    },
    
    // Mark as seen - call backend, UI updates on next poll
    markAsSeen: async (notificationIds: string[]) => {
      if (notificationIds.length === 0) return;
      
      // Call backend API to persist the change
      // UI will update when next GetNotifications poll occurs
      try {
        await FinSpaceServiceApi.markNotificationsAsSeen({ notificationIds });
        logger.info('Successfully marked notifications as seen, will update on next poll');
      } catch (error) {
        logger.error('Failed to mark notifications as seen:', error as Error);
        set({ error: 'Failed to mark notifications as seen' });
        throw error; // Allow caller to handle the failure
      }
    },
    
    // Mark all as read - marks all unread notifications as seen
    
    getUnreadNotifications: () => {
      return get().notifications.filter(n => !n.seenAt);
    },
    
    // Refresh notifications from backend (respects backend as source of truth)
    refreshUnreadCount: async () => {
      try {
        const response = await FinSpaceServiceApi.getNotifications();
        const freshNotifications = response.notifications || [];

        set(state => {
          // Create a map of fresh notifications by ID for quick lookup
          const freshMap = new Map(freshNotifications.map(n => [n.id, n]));

          // Keep older paginated notifications that aren't in the fresh batch
          const olderNotifications = state.notifications.filter(n => !freshMap.has(n.id));

          // Merge: fresh notifications first (newest), then older paginated ones
          const mergedNotifications = [...freshNotifications, ...olderNotifications];
          
          // Only count unread from fresh notifications (not older paginated ones)
          // This ensures the badge only reflects new unread notifications
          const unreadCount = freshNotifications.filter(n => !n.seenAt).length;

          return {
            notifications: mergedNotifications,
            unreadCount,
            recommendedNextPollingIntervalSeconds: response.recommendedNextPollingIntervalSeconds || 300,
          };
        });

        // Always restart polling to continue the cycle
        get().stopPolling();
        get().startPolling();
      } catch (error) {
        logger.error('Failed to refresh notifications:', error as Error);
        // Even on error, restart polling to keep trying
        get().stopPolling();
        get().startPolling();
      }
    },
    
    // Start automatic polling
    startPolling: () => {
      const { pollingTimerId, recommendedNextPollingIntervalSeconds } = get();
      
      // Clear any existing timer
      if (pollingTimerId) {
        clearTimeout(pollingTimerId);
      }
      
      // Set up new polling timer
      const timerId = setTimeout(() => {
        get().refreshUnreadCount();
      }, recommendedNextPollingIntervalSeconds * 1000);
      
      set({ pollingTimerId: timerId });
    },
    
    // Stop automatic polling
    stopPolling: () => {
      const { pollingTimerId } = get();
      
      if (pollingTimerId) {
        clearTimeout(pollingTimerId);
        set({ pollingTimerId: null });
      }
    },
  })
);
